import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Eye, Tag, AlertCircle, Calendar, User } from "lucide-react";
import api, { formatErr } from "../../lib/api";

const SECTION_LABEL = {
  vintage_luxury: "Vintage & Luxury",
  sport_chronograph: "Sport & Chronograph",
  new_arrivals: "New Arrivals",
  on_sale: "On Sale",
};

export default function AdmApprovals() {
  const [items, setItems] = useState([]);
  const [previewing, setPreviewing] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  const refresh = async () => {
    try { const r = await api.get("/admin/products/pending"); setItems(r.data); }
    catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { refresh(); }, []);

  const approve = async (p) => {
    try { await api.post(`/admin/products/${p.id}/approve`); toast.success(`${p.name} is now live`); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const reject = async () => {
    if (!rejecting) return;
    try {
      await api.post(`/admin/products/${rejecting.id}/reject`, { reason });
      toast.success("Marked as needing revision");
      setRejecting(null); setReason(""); refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="overline mb-2">Catalog Workflow</div>
        <h1 className="font-display text-4xl text-white">Pending Approvals</h1>
        <p className="text-sm text-white/50 mt-2">Products submitted by shop assistants. Approve to publish on the storefront, or reject with a reason for revision.</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="card-luxe overflow-hidden" data-testid={`pending-${p.id}`}>
            <div className="aspect-square bg-[#070707] relative">
              {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=900"; }}/>}
              <span className="absolute top-2 left-2 text-[9px] uppercase tracking-luxe bg-copper text-white px-2 py-0.5">Pending</span>
              {p.images?.length > 1 && <span className="absolute top-2 right-2 text-[9px] bg-black/70 text-white px-2 py-0.5">+{p.images.length - 1}</span>}
            </div>
            <div className="p-4">
              <div className="font-display text-lg text-white truncate">{p.name}</div>
              <div className="text-xs text-copper uppercase tracking-luxe mt-1">{p.brand} · {p.category}</div>
              <div className="flex justify-between items-end mt-2 gap-2 flex-wrap">
                <div>
                  <div className="text-white text-base">AED {p.price?.toLocaleString()}</div>
                  {p.original_price && p.original_price > p.price && <div className="text-xs text-white/40 line-through">AED {p.original_price.toLocaleString()}</div>}
                </div>
                <div className="text-xs text-white/50">Stock: {p.stock || 0}</div>
              </div>
              {p.sections?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {p.sections.map((sec) => <span key={sec} className="text-[9px] uppercase tracking-luxe bg-white/5 text-white/70 px-2 py-0.5 flex items-center gap-1"><Tag size={9}/>{SECTION_LABEL[sec] || sec}</span>)}
                </div>
              )}
              <div className="text-[10px] text-white/40 mt-3 flex items-center gap-3 flex-wrap">
                <span className="flex items-center gap-1"><User size={10}/>{p.created_by_email || "—"}</span>
                <span className="flex items-center gap-1"><Calendar size={10}/>{new Date(p.created_at).toLocaleDateString()}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <button data-testid={`view-${p.id}`} onClick={() => setPreviewing(p)} className="btn-outline text-xs justify-center"><Eye size={11}/></button>
                <button data-testid={`reject-${p.id}`} onClick={() => { setRejecting(p); setReason(""); }} className="text-xs uppercase tracking-luxe px-3 py-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 flex items-center justify-center gap-1"><X size={11}/> Reject</button>
                <button data-testid={`approve-${p.id}`} onClick={() => approve(p)} className="text-xs uppercase tracking-luxe px-3 py-2 border border-[#32D74B]/40 text-[#32D74B] hover:bg-[#32D74B]/10 flex items-center justify-center gap-1"><Check size={11}/> Approve</button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="card-luxe p-12 text-center text-white/40 col-span-full">
          <AlertCircle size={32} className="mx-auto mb-3 text-white/20"/>
          No products pending approval
        </div>}
      </div>

      {/* Preview modal */}
      {previewing && (
        <div data-testid="preview-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 maintenance-blur" onClick={() => setPreviewing(null)}>
          <div className="card-luxe p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="overline">Pending Submission</div>
                <h2 className="font-display text-2xl mt-1">{previewing.name}</h2>
                <div className="text-xs text-white/50 mt-1">By {previewing.created_by_email}</div>
              </div>
              <button onClick={() => setPreviewing(null)} className="p-2 hover:text-copper"><X size={16}/></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
              {previewing.images?.map((src, i) => <img key={i} src={src} alt="" className="aspect-square object-cover bg-[#070707] border border-white/10"/>)}
            </div>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <div><span className="text-white/40">Brand:</span> <span className="text-white">{previewing.brand}</span></div>
              <div><span className="text-white/40">Category:</span> <span className="text-white">{previewing.category}</span></div>
              <div><span className="text-white/40">Price:</span> <span className="text-copper">AED {previewing.price}</span></div>
              <div><span className="text-white/40">Stock:</span> <span className="text-white">{previewing.stock}</span></div>
              <div><span className="text-white/40">SKU:</span> <span className="text-white">{previewing.sku || "—"}</span></div>
              <div><span className="text-white/40">Movement:</span> <span className="text-white">{previewing.movement || "—"}</span></div>
              <div className="md:col-span-2"><span className="text-white/40">Description:</span> <p className="text-white/80 mt-1 whitespace-pre-line">{previewing.description}</p></div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => { approve(previewing); setPreviewing(null); }} className="btn-primary"><Check size={14}/> Approve & Publish</button>
              <button onClick={() => { setRejecting(previewing); setPreviewing(null); }} className="btn-outline">Reject</button>
            </div>
          </div>
        </div>
      )}

      {/* Reject reason modal */}
      {rejecting && (
        <div data-testid="reject-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 maintenance-blur" onClick={() => setRejecting(null)}>
          <div className="card-luxe p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-2xl mb-4">Reject {rejecting.name}?</div>
            <p className="text-xs text-white/55 mb-3">The shop assistant will be notified by email with this reason and can edit + resubmit.</p>
            <textarea data-testid="reject-reason" className="input-box" rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for rejection (e.g. Image quality too low, missing case material…)"/>
            <div className="flex gap-3 mt-4">
              <button data-testid="reject-confirm" onClick={reject} className="btn-primary"><X size={13}/> Confirm Rejection</button>
              <button onClick={() => setRejecting(null)} className="btn-outline">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
